import {
  TreatmentComponentStateContext,
  useTreatmentComponentState,
} from "@/services/backend/rlt/treatments/components/treatment-component-state-context";
import { PropsWithChildren, useEffect, useMemo, useState } from "react";
import {
  Column,
  TreatmentComponentState,
} from "@/services/backend/rlt/treatments/lib/treatment-component-state";
import { useFilterAvailableColumnsForRole } from "@/services/backend/rlt/treatments/hooks/use-filter-available-columns-for-role";

interface Props extends PropsWithChildren {
  availableColumns: Column[];
  defaultColumns: Column[];
}

export function TreatmentComponentStateContextProvider({
  children,
  availableColumns,
  defaultColumns,
  disabled = false,
}: Props & { disabled?: boolean }) {
  const [state, setState] = useState<TreatmentComponentState>({
    availableColumns,
    columns: defaultColumns.filter((column) =>
      availableColumns.includes(column),
    ),
  });
  const value = useMemo(
    () => ({ state, setState, disabled }),
    [state, setState, disabled],
  );

  return (
    <TreatmentComponentStateContext.Provider value={value}>
      <TreatmentComponentColumnFilter
        availableColumns={availableColumns}
        defaultColumns={defaultColumns}
      >
        {children}
      </TreatmentComponentColumnFilter>
    </TreatmentComponentStateContext.Provider>
  );
}

function TreatmentComponentColumnFilter({
  children,
  availableColumns,
  defaultColumns,
}: Props) {
  const roleFilteredAvailableColumns =
    useFilterAvailableColumnsForRole(availableColumns);
  const { state, setState } = useTreatmentComponentState();

  useEffect(() => {
    if (
      state.availableColumns.every(
        (col, index) => roleFilteredAvailableColumns[index] === col,
      )
    ) {
      return;
    }
    setState({
      ...state,
      availableColumns: roleFilteredAvailableColumns,
      columns: defaultColumns.filter((column) =>
        roleFilteredAvailableColumns.includes(column),
      ),
    });
  }, [defaultColumns, roleFilteredAvailableColumns, setState, state]);

  return children;
}
